import { ReactNode } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { LifecycleNudgeBanner } from '@/components/Billing/LifecycleNudgeBanner';

interface AppLayoutProps {
  children: ReactNode;
}

const navigationItems = [
  { label: 'Overview', url: '/' },
  { label: 'Products', url: '/products' },
  { label: 'Billing', url: '/billing' },
  { label: 'Settings', url: '/settings' },
];

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.25),_transparent_55%)] from-white via-white to-[#eef2ff] text-slate-900">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(125,211,252,0.25),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(167,139,250,0.2),transparent_45%)]" />
      <div className="relative z-10 mx-auto w-full max-w-6xl overflow-x-hidden px-3 pb-16 pt-6 sm:px-6 lg:px-8">
        <header className="glass-panel flex flex-col gap-4 p-4 sm:p-6 sm:gap-6 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-6">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
                AR Space Commerce
              </p>
              <h1 className="mt-2 text-xl font-semibold text-slate-900 sm:mt-3 sm:text-3xl">
                {navigationItems.find((n) => n.url === location.pathname || (n.url !== '/' && location.pathname.startsWith(n.url)))?.label ?? 'Command & Billing Hub'}
              </h1>
              <p className="mt-1 text-sm text-slate-500 hidden sm:block">
                Manage AR generation, subscriptions, and experience settings with the same calm visual system everywhere.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 rounded-2xl border border-white/70 bg-white/70 p-1.5 sm:p-2">
              {navigationItems.map((item) => {
                const isSelected =
                  item.url === location.pathname ||
                  (item.url !== '/' && location.pathname.startsWith(item.url));

                return (
                  <button
                    key={item.url}
                    type="button"
                    onClick={() => navigate(item.url)}
                    className={cn(
                      'relative rounded-xl px-3 py-1.5 text-sm font-semibold transition-colors sm:rounded-2xl sm:px-4 sm:py-2',
                      isSelected
                        ? 'bg-gradient-to-r from-indigo-500 to-sky-500 text-white shadow-lg shadow-indigo-200'
                        : 'text-slate-500 hover:text-slate-900'
                    )}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        </header>

        {/* Below the nav, above page content: the merchant sees it on every
            authenticated route without it ever covering navigation. Renders
            null whenever there is no active nudge, which is the common case. */}
        <div className="mt-6 empty:mt-0 sm:mt-10 sm:empty:mt-0">
          <LifecycleNudgeBanner />
        </div>

        <main className="page-grid mt-6 sm:mt-10">{children}</main>

        {/* Footer with legal links */}
        <footer className="mt-16 border-t border-slate-200/50 pt-8 pb-6">
          <div className="flex flex-col items-center justify-center gap-4 text-sm text-slate-500 sm:flex-row sm:gap-6">
            <p className="text-xs text-slate-400">
              © {new Date().getFullYear()} AR Space Commerce. All rights reserved.
            </p>
            <div className="flex gap-4">
              <Link
                to="/privacy"
                className="transition-colors hover:text-slate-700 underline-offset-4 hover:underline"
              >
                Privacy Policy
              </Link>
              <span className="text-slate-300">•</span>
              <Link
                to="/terms"
                className="transition-colors hover:text-slate-700 underline-offset-4 hover:underline"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
